/**
 * Typed event registry for PostHog analytics.
 *
 * Every PostHog event MUST be defined here with a Zod schema.
 * Untyped / ad-hoc events are FORBIDDEN.
 *
 * Adding a new event:
 *  1. Define the schema in the appropriate category section below
 *  2. Add it to the EventRegistry at the bottom
 *  3. Use track('EVENT_NAME', { ... }) — TypeScript enforces correctness
 *
 * See Part 22 §121 of the Tech Guidelines.
 */

import { z } from 'zod';

/**
 * How someone signs in or up: an email and password, or one of the social
 * providers — `SOCIAL_PROVIDERS` in @tonnie/foundation, which this leaf package
 * cannot import. The portals pass a `SocialProvider` as `method`, so a provider
 * missing here fails their typecheck.
 */
const authMethod = z.enum(['email', 'google', 'apple', 'microsoft', 'linkedin']);

// ═══════════════════════════════════════════════════════════════════════
// AUTH / CONTEXT
// ═══════════════════════════════════════════════════════════════════════

export const AuthEvents = {
  AUTH_SIGNED_IN: z.object({
    method: z.enum(['clerk', 'sso', 'magic_link']),
  }),
  AUTH_SIGNED_OUT: z.object({}),
  CONTEXT_SWITCHED: z.object({
    from_tenant_id: z.string().optional(),
    to_tenant_id: z.string(),
    from_persona: z.string().optional(),
    to_persona: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════════════════

export const LoginEvents = {
  /** User submitted the login form (email/password, or a social provider) */
  AUTH_LOGIN_SUBMITTED: z.object({
    method: authMethod,
  }),
  /** Login succeeded — session is now active */
  AUTH_LOGIN_SUCCEEDED: z.object({
    method: authMethod,
  }),
  /** Login attempt failed (wrong credentials, Clerk error, etc.) */
  AUTH_LOGIN_FAILED: z.object({
    method: authMethod,
    error_code: z.string().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// SIGNUP
// ═══════════════════════════════════════════════════════════════════════

export const SignupEvents = {
  /** User submitted the signup registration form */
  AUTH_SIGNUP_SUBMITTED: z.object({
    method: authMethod,
  }),
  /** Email verification OTP was sent to the user */
  AUTH_SIGNUP_VERIFICATION_SENT: z.object({}),
  /** User completed email verification successfully */
  AUTH_SIGNUP_VERIFICATION_COMPLETED: z.object({}),
  /** User requested to resend the verification code */
  AUTH_SIGNUP_VERIFICATION_RESENT: z.object({}),
  /** Signup succeeded — session is now active */
  AUTH_SIGNUP_SUCCEEDED: z.object({
    method: authMethod,
  }),
  /** Signup attempt failed */
  AUTH_SIGNUP_FAILED: z.object({
    method: authMethod,
    error_code: z.string().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// ONBOARDING
// ═══════════════════════════════════════════════════════════════════════

/** What a CV / LinkedIn PDF import found — shared by onboarding and profile. */
const importedFields = z.object({
  firstName: z.boolean(),
  lastName: z.boolean(),
  role: z.boolean(),
  experience: z.number(),
  education: z.number(),
  languages: z.number(),
  skills: z.number(),
  certifications: z.number(),
  websiteUrls: z.number(),
});

export const OnboardingEvents = {
  /** User entered the onboarding wizard */
  ONBOARDING_STARTED: z.object({
    mode: z.enum(['light', 'linkedin', 'full']),
  }),
  /** User advanced to a specific wizard step */
  ONBOARDING_STEP_VIEWED: z.object({
    step_id: z.string(),
    step_index: z.number(),
    total_steps: z.number(),
  }),
  /** User completed a wizard step (clicked next) */
  ONBOARDING_STEP_COMPLETED: z.object({
    step_id: z.string(),
    step_index: z.number(),
    total_steps: z.number(),
  }),
  /** User skipped the onboarding to dashboard */
  ONBOARDING_SKIPPED: z.object({
    step_id: z.string(),
    step_index: z.number(),
    total_steps: z.number(),
  }),
  /** User completed the entire onboarding wizard */
  ONBOARDING_COMPLETED: z.object({
    mode: z.enum(['light', 'linkedin', 'full']),
    total_steps: z.number(),
    linkedin_connected: z.boolean(),
  }),
  /** User connected LinkedIn during onboarding */
  ONBOARDING_LINKEDIN_CONNECTED: z.object({}),
  /** User uploaded a LinkedIn PDF for auto-fill */
  ONBOARDING_LINKEDIN_PDF_UPLOADED: z.object({ fieldsImported: importedFields }),
  /** Onboarding completion API call failed */
  ONBOARDING_FAILED: z.object({
    step_id: z.string(),
    error_type: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// PROFILE (Settings → Profile sections · citizen first run slice F)
// ═══════════════════════════════════════════════════════════════════════

export const ProfileEvents = {
  /**
   * The citizen saved one section of their profile. How complete it is lives
   * on the server (profile strength), not in the event.
   */
  PROFILE_SECTION_SAVED: z.object({
    section: z.enum(['identity', 'mobility', 'skills', 'education', 'experience']),
  }),
  /** The citizen confirmed a passport / ID card into their profile */
  PROFILE_IDENTITY_DOCUMENT_CONFIRMED: z.object({
    documentType: z.enum(['passport', 'id_card']),
    fieldsApplied: z.number().int().nonnegative(),
  }),
  /** The citizen imported a CV / LinkedIn PDF into their profile */
  PROFILE_IMPORT_COMPLETED: z.object({ fieldsImported: importedFields }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY
// ═══════════════════════════════════════════════════════════════════════

export const JourneyEvents = {
  JOURNEY_CREATED: z.object({
    journey_id: z.string(),
    origin_country: z.string(),
    destination_country: z.string(),
    scenario: z.string(),
  }),
  JOURNEY_ACTIVATED: z.object({
    journey_id: z.string(),
    unlock_mode: z.enum(['free', 'paid', 'sponsored']),
  }),
  SUBSCRIPTION_CHECKOUT_STARTED: z.object({
    journey_id: z.string(),
    plan: z.string(),
    price_id: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// CHECKUP
// ═══════════════════════════════════════════════════════════════════════

export const CheckUpEvents = {
  CHECKUP_STARTED: z.object({
    journey_id: z.string(),
    talent_id: z.string(),
  }),
  CHECKUP_CHAT_COMPLETED: z.object({
    journey_id: z.string(),
    talent_id: z.string(),
    message_count: z.number(),
  }),
  CHECKUP_COMPLETED: z.object({
    journey_id: z.string(),
    talent_id: z.string(),
    iqulture_score: z.number().min(0).max(100),
    badge: z.enum(['NOOB', 'POTENTIAL', 'PRO']),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// TELEPORTAL
// ═══════════════════════════════════════════════════════════════════════

export const TelePortalEvents = {
  TELEPORTAL_VIEWED: z.object({
    journey_id: z.string(),
    track: z.string(),
  }),
  TELEPORTAL_TASK_COMPLETED: z.object({
    task_id: z.string(),
    task_type: z.string(),
    track_type: z.string(),
  }),
  TELEPORTAL_DOCUMENT_UPLOADED: z.object({
    document_type: z.string(),
    file_size_bytes: z.number(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// CARE
// ═══════════════════════════════════════════════════════════════════════

export const CareEvents = {
  CARE_DAILY_CHECKIN_COMPLETED: z.object({
    checkin_type: z.enum(['mood', 'quick']),
    mood_score: z.number().min(1).max(5).optional(),
  }),
  CARE_CHECKPOINT_COMPLETED: z.object({
    checkpoint_type: z.string(),
    pillar: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// QULTUREUP
// ═══════════════════════════════════════════════════════════════════════

export const QultureUpEvents = {
  QULTUREUP_MISSION_STARTED: z.object({
    mission_id: z.string(),
    world_id: z.string(),
  }),
  QULTUREUP_MISSION_COMPLETED: z.object({
    mission_id: z.string(),
    world_id: z.string(),
    score: z.number().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// CITIZEN (MOBILE-SPECIFIC)
// ═══════════════════════════════════════════════════════════════════════

export const CitizenEvents = {
  COPILOT_MESSAGE_SENT: z.object({
    mode: z.enum(['guide', 'brain', 'agent']),
  }),
  COPILOT_MESSAGE_RECEIVED: z.object({
    mode: z.enum(['guide', 'brain', 'agent']),
    response_time_ms: z.number(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// BUSINESS
// ═══════════════════════════════════════════════════════════════════════

export const BusinessEvents = {
  ATS_CONNECTED: z.object({
    provider: z.string(),
  }),
  CANDIDATE_INVITED: z.object({
    talent_id: z.string(),
    program_id: z.string().optional(),
  }),
  DASHBOARD_VIEWED: z.object({
    dashboard_id: z.string(),
  }),
  JOURNEY_SPONSORED: z.object({
    talent_id: z.string(),
    plan: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// BACKEND OUTCOME EVENTS (SERVER-SIDE ONLY)
// ═══════════════════════════════════════════════════════════════════════

export const BackendOutcomeEvents = {
  SUBSCRIPTION_ACTIVATED: z.object({
    stripe_subscription_id: z.string(),
    plan: z.string(),
  }),
  ATS_SYNC_COMPLETED: z.object({
    provider: z.string(),
    candidates_synced: z.number(),
    duration_ms: z.number(),
  }),
  IQULTURE_SCORE_GENERATED: z.object({
    talent_id: z.string(),
    score: z.number(),
    badge: z.string(),
    calculation_duration_ms: z.number(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// FEEDBACK
// ═══════════════════════════════════════════════════════════════════════

export const FeedbackEvents = {
  NPS_SUBMITTED: z.object({
    score: z.number().min(0).max(10),
    trigger: z.string(),
  }),
  FEEDBACK_FEATURE_SUBMITTED: z.object({
    category: z.string(),
    hero: z.string().optional(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// PAGE VIEW (AUTO-TRACKED)
// ═══════════════════════════════════════════════════════════════════════

export const NavigationEvents = {
  PAGE_VIEWED: z.object({
    page: z.string(),
  }),
} as const;

// ═══════════════════════════════════════════════════════════════════════
// MASTER REGISTRY
// ═══════════════════════════════════════════════════════════════════════

/**
 * The single source of truth for all trackable events.
 *
 * To add a new event:
 *  1. Define its Zod schema in the appropriate category above
 *  2. Spread the category into this registry
 *  3. The track() function will enforce type safety at compile time
 *     and validate properties at runtime via Zod
 */
// ═══════════════════════════════════════════════════════════════════════
// COHORT FUNNEL (server-side, PERSON-ANONYMOUS)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Cohort-level progression, for the funnel a business operator builds in
 * PostHog. SERVER-SIDE ONLY.
 *
 * ─── Why these carry NO person identifier ─────────────────────────────────
 *
 * The platform has no PostHog erasure path. `CheckUpEvents.CHECKUP_COMPLETED`
 * and `BackendOutcomeEvents.IQULTURE_SCORE_GENERATED` both require a
 * `talent_id`, so emitting them from the server would put a person identifier
 * into a system that account deletion cannot reach — reintroducing exactly the
 * PII-survives-deletion class `purgeCopilotFootprint` was written to fix.
 *
 * The operator's question is "how is this cohort progressing", which is a
 * COUNTING question and needs no person. So these are keyed on the tenant,
 * carry the cohort, and carry nothing that identifies a human. The per-person
 * truth stays in the cohort-snapshot API, which is access-controlled and
 * covered by the deletion cascade.
 *
 * If per-person funnels are ever genuinely needed, the prerequisite is a
 * PostHog erasure call wired into `softCascadeUserFootprint` — not a quiet
 * widening of these payloads.
 */
export const CohortFunnelEvents = {
  /** A company asked someone for an assessment. */
  COHORT_INVITATION_SENT: z.object({
    cohort_id: z.string(),
    program_id: z.string().optional(),
    invitation_kind: z.string(),
  }),
  /** Someone accepted and their journey went live. */
  COHORT_INVITATION_ACCEPTED: z.object({
    cohort_id: z.string(),
    /** Hours from send to accept — the funnel's latency, with no person in it. */
    hours_to_accept: z.number().optional(),
  }),
  COHORT_CHECKUP_STARTED: z.object({
    cohort_id: z.string().optional(),
  }),
  COHORT_CHECKUP_SUBMITTED: z.object({
    cohort_id: z.string().optional(),
  }),
  /** Scored. The score is a cohort-level metric here, not a person's record. */
  COHORT_CHECKUP_SCORED: z.object({
    cohort_id: z.string().optional(),
    score: z.number().min(0).max(100),
    badge: z.string(),
  }),
} as const;

export const EventRegistry = {
  ...AuthEvents,
  ...LoginEvents,
  ...SignupEvents,
  ...OnboardingEvents,
  ...ProfileEvents,
  ...JourneyEvents,
  ...CheckUpEvents,
  ...TelePortalEvents,
  ...CareEvents,
  ...QultureUpEvents,
  ...CitizenEvents,
  ...BusinessEvents,
  ...BackendOutcomeEvents,
  ...CohortFunnelEvents,
  ...FeedbackEvents,
  ...NavigationEvents,
} as const;

/** Union type of all valid event names */
export type EventName = keyof typeof EventRegistry;

/** Helper type: infer properties for a specific event */
export type EventProperties<E extends EventName> = z.infer<(typeof EventRegistry)[E]>;
